import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import client from './api/client';
import './Navigation.css';

const ANALYSIS_PATHS = ['/skillfarm', '/deals', '/price-analysis'];
const SEEDING_PATHS = ['/seeding', '/contracts'];

const Swatch = ({ area }) => (
  <span className="nav-swatch" style={{ background: `var(--area-${area})` }} />
);

function Navigation() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const analysisRef = useRef(null);

  const analysisActive = ANALYSIS_PATHS.some(path =>
    location.pathname.startsWith(path)
  );
  const seedingActive = SEEDING_PATHS.some(path =>
    location.pathname.startsWith(path)
  );
  const [seedingOpen, setSeedingOpen] = React.useState(false);
  const seedingRef = useRef(null);

  useEffect(() => {
    if (!seedingOpen) return;
    function handleClickOutside(e) {
      if (seedingRef.current && !seedingRef.current.contains(e.target)) {
        setSeedingOpen(false);
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setSeedingOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [seedingOpen]);

  useEffect(() => {
    if (!analysisOpen) return;
    function handleClickOutside(e) {
      if (analysisRef.current && !analysisRef.current.contains(e.target)) {
        setAnalysisOpen(false);
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setAnalysisOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [analysisOpen]);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await client.get('/auth/user/');
      if (response.data.authenticated) {
        setUser(response.data.character);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUser(null);
    }
  }, []);

  const handleEveLogin = async () => {
    setAuthLoading(true);
    try {
      // Get current page path to redirect back to after login
      const currentPath = window.location.pathname;
      const response = await client.get(`/auth/eve/login/?redirect=${encodeURIComponent(currentPath)}`);
      window.location.href = response.data.login_url;
    } catch (error) {
      console.error('Error starting EVE login:', error);
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Get CSRF token first
      const csrfResponse = await client.get('/auth/csrf/');
      const csrfToken = csrfResponse.data.csrfToken;

      // Send logout request with CSRF token
      await client.post('/auth/logout/',
        { csrfmiddlewaretoken: csrfToken },
        {
          headers: {
            'X-CSRFToken': csrfToken
          }
        }
      );
      // Full reload so pages drop any data fetched while signed in
      window.location.href = '/';
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle auth callback results
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_success') === 'true') {
      // Clear URL parameters and refresh user info
      window.history.replaceState({}, '', window.location.pathname);
      fetchUserInfo();
    } else if (params.get('auth_error')) {
      window.history.replaceState({}, '', window.location.pathname);
      setAuthLoading(false);
    }
  }, [fetchUserInfo]);

  // Fetch user info on component mount
  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  return (
    <nav className="navigation">
        <div className="nav-left">
          <Link to="/" className="nav-title">
            <svg viewBox="0 0 18 18" aria-hidden="true">
              <path d="M9 1.5 16 15H2Z" fill="none" stroke="var(--accent)" strokeWidth="1.6" />
              <path d="M9 7.5 12 13H6Z" fill="var(--area-reports)" />
            </svg>
            <span>Aestimatio</span>
          </Link>
          <div
            className={`nav-dropdown ${seedingActive ? 'active' : ''}`}
            ref={seedingRef}
          >
            <button
              className={`nav-link nav-dropdown-toggle ${seedingActive ? 'active' : ''}`}
              onClick={() => setSeedingOpen(o => !o)}
            >
              <Swatch area="seeding" />Seeding<span className="nav-caret">▾</span>
            </button>
            {seedingOpen && (
              <div className="nav-dropdown-menu">
                <Link
                  to="/seeding"
                  className={`nav-dropdown-item ${location.pathname.startsWith('/seeding') ? 'active' : ''}`}
                  onClick={() => setSeedingOpen(false)}
                >
                  <Swatch area="seeding" />Market Seeding
                </Link>
                <Link
                  to="/contracts"
                  className={`nav-dropdown-item ${location.pathname.startsWith('/contracts') ? 'active' : ''}`}
                  onClick={() => setSeedingOpen(false)}
                >
                  <Swatch area="contracts" />Contract Seeding
                </Link>
              </div>
            )}
          </div>
          <Link
            to="/lp-tracking"
            className={`nav-link ${location.pathname === '/lp-tracking' ? 'active' : ''}`}
          >
            <Swatch area="lp" />LP Tracking
          </Link>
          {user && (
            <Link
              to="/reports"
              className={`nav-link ${location.pathname === '/reports' ? 'active' : ''}`}
            >
              <Swatch area="reports" />Reports
            </Link>
          )}
          {user && (
            <Link
              to="/investments"
              className={`nav-link ${location.pathname === '/investments' ? 'active' : ''}`}
            >
              <Swatch area="investments" />Investments
            </Link>
          )}
          <div
            className={`nav-dropdown ${analysisActive ? 'active' : ''}`}
            ref={analysisRef}
          >
            <button
              className={`nav-link nav-dropdown-toggle ${analysisActive ? 'active' : ''}`}
              onClick={() => setAnalysisOpen(o => !o)}
            >
              <Swatch area="analysis" />Analysis<span className="nav-caret">▾</span>
            </button>
            {analysisOpen && (
              <div className="nav-dropdown-menu">
                <Link
                  to="/skillfarm"
                  className={`nav-dropdown-item ${location.pathname === '/skillfarm' ? 'active' : ''}`}
                  onClick={() => setAnalysisOpen(false)}
                >
                  Skill Farm
                </Link>
                <Link
                  to="/deals"
                  className={`nav-dropdown-item ${location.pathname === '/deals' ? 'active' : ''}`}
                  onClick={() => setAnalysisOpen(false)}
                >
                  Deal Analysis
                </Link>
                <Link
                  to="/price-analysis"
                  className={`nav-dropdown-item ${location.pathname === '/price-analysis' ? 'active' : ''}`}
                  onClick={() => setAnalysisOpen(false)}
                >
                  Price Analysis
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className="nav-right">
          {user ? (
            <>
              <Link to="/profile" className="character-portrait-container">
                <img
                  src={user.portrait_128x128}
                  alt={user.name}
                  className="character-portrait"
                  onError={(e) => {
                    // Fallback to smaller portrait if 128x128 fails
                    e.target.src = user.portrait_64x64;
                  }}
                />
                <div className="character-details">
                  <span className="character-name">{user.name}</span>
                  {user.corporation_name && (
                    <span className="corporation-name">{user.corporation_name}</span>
                  )}
                </div>
              </Link>
              <Link to="/settings" className="icon-btn" title="Account settings" aria-label="Account settings">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
                  <circle cx="8" cy="8" r="2.2" />
                  <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
                </svg>
              </Link>
              <button onClick={handleLogout} className="btn btn-ghost nav-signout">
                Sign out
              </button>
            </>
          ) : (
            <button
              onClick={handleEveLogin}
              disabled={authLoading}
              className="eve-sso-btn"
            >
              {authLoading ? (
                'Connecting...'
              ) : (
                <img
                  src="https://web.ccpgamescdn.com/eveonlineassets/developers/eve-sso-login-white-large.png"
                  alt="Login with EVE Online"
                  className="eve-sso-image"
                />
              )}
            </button>
          )}
        </div>
    </nav>
  );
}

export default Navigation;